<template>
  <div
    ref="marketListRef"
    class="m-market-list"
    :class="{
      'favorite-guide': showGuideImg
    }"
  >
    <template v-if="!loading">
      <template v-if="show">
        <template v-for="id in sortedMarketIds">
          <MarketItem
            ref="marketItemRef"
            :key="id"
            :group-name="name"
            :market-id="id"
            :market="event.markets.map[id]"
            :should-expand="shouldMarketExpand(id)"
            @favourite="onFavourite"
            @toggle-expand="handleToggleExpand"
            @active-change="handleMarketItemActiveChange"
          />
        </template>
      </template>

      <template v-else-if="showGuideImg">
        <div class="img-container">
          <img :src="favouriteImg" />
        </div>
      </template>

      <template v-else>
        <NoMarkets />
      </template>
    </template>

    <template v-else>
      <div
        v-block-loading="loading"
        :style="{ height: `${marketListPrevHeight}px` }"
        class="market-list-loading-mask"
      />
    </template>
  </div>
</template>

<script>
import { computed, ref, onMounted, onBeforeUnmount, onActivated, onDeactivated, nextTick } from '@vue/composition-api'
import { storeToRefs } from 'pinia'
import { useMatchDetailStore } from 'wap/stores/matchDetail'
import MarketItem from './MarketItem'
import NoMarkets from './NoMarkets'
import { useExpandedMarkets } from 'composables/useExpandedMarkets.js'
import { isLiveMatch, isActiveMarket } from '@ms/components/match/status.js'
import eventBus from '@ms/utils/event-bus.js'
import { getTopic } from 'custom-push/lib/pushTopic'
import { useGlobalConfigStore } from '@ms/stores/globalConfig'
import { useThemeImage } from 'composables/useThemeImage'

export default {
  name: 'MarketList',
  components: {
    MarketItem,
    NoMarkets
  },
  props: {
    marketIds: {
      type: Array,
      default: () => []
    },
    // 列表 group name
    name: {
      type: String,
      default: ''
    }
  },
  setup(props) {
    const isActivated = ref(false)
    const lastScrollY = ref(null)

    const loading = ref(false)

    const marketListRef = ref(null)
    const marketListPrevHeight = ref(0)

    const marketItemRef = ref([])
    const firstVisibleMarketItemIndex = ref(null)
    const stickedHeaderHeight = ref(0)
    let abortController = null

    const matchDetailStore = useMatchDetailStore()
    const { event, subscribedIgpomsTopicSet } = storeToRefs(matchDetailStore)

    const { expandedMarketGroupMap, activeMarketIds, toggleMarketExpand, collapseAllMarkets, expandAllMarkets, setMarketActive, setMarketInactive } = useExpandedMarkets()
    const expandedMarketIdList = computed(() => expandedMarketGroupMap.value[props.name] ?? [])
    const handleToggleExpand = (options = {}) => {
      const { marketId } = options

      toggleMarketExpand({
        marketGroup: props.name,
        marketId
      })
    }
    const shouldMarketExpand = (targetMarketId) => {
      return expandedMarketIdList.value.some((marketId) => {
        return marketId === targetMarketId
      })
    }

    // 排序规则：priority 从小到大，若相同，则按 market id 从小到大
    const sortedMarketIds = computed(() => {
      const { marketIds } = props
      if (!marketIds.length) return []
      const mIds = [...marketIds]
      const marketMap = event.value.markets.map
      mIds.sort((a, b) => {
        const marketA = Array.isArray(marketMap[a]) ? marketMap[a][0] : marketMap[a]
        const marketB = Array.isArray(marketMap[b]) ? marketMap[b][0] : marketMap[b]
        if (typeof marketA?.priority !== 'number' || typeof marketB?.priority !== 'number') return 0
        const diff = marketA.priority - marketB.priority
        if (diff > 0) return 1
        else if (diff < 0) return -1
        else {
          return parseInt(a) - parseInt(b)
        }
      })
      return mIds
    })

    const fetchMarketInfoBatchBody = computed(() => {
      return unSubscribedMarketIds.value.map((marketId) => {
        const market = event.value.markets.map[marketId]
        const isSpecifiers = Array.isArray(market)
        const marketInfo = isSpecifiers ? market[0] : market

        const obj = {
          marketId: marketInfo.id,
          sportId: event.value.sportId,
          eventId: event.value.eventId,
          product: isLiveMatch(event.value.status) ? 1 : 3
        }

        obj.specifiers = marketInfo.specifiers ?? ''

        return obj
      })
    })

    const handleMarketItemActiveChange = (options = {}) => {
      const { marketId, active } = options

      if (active) {
        setMarketActive(marketId)
        return
      }

      setMarketInactive(marketId)
    }

    const { igopmsMarkets } = storeToRefs(useGlobalConfigStore())

    const sortedActiveMarketIds = computed(() => {
      return sortedMarketIds.value.filter((marketId) => {
        return activeMarketIds.value.includes(marketId)
      })
    })

    const blackListedMarketIds = computed(() => {
      return sortedActiveMarketIds.value.filter((marketSymbolId) => {
        const [marketId] = marketSymbolId.toString().split('_')
        return igopmsMarkets.value.includes(marketId)
      })
    })

    const unSubscribedMarketIds = computed(() => {
      return blackListedMarketIds.value.filter((marketId) => {
        const market = event.value.markets.map[marketId]
        const isSpecifiers = Array.isArray(market)
        const marketInfo = isSpecifiers ? market[0] : market

        const topic = getTopic(
          {
            sportId: event.value.sportId,
            categoryId: event.value.categoryId,
            tournamentId: event.value.tournamentId,
            eventId: event.value.eventId,
            specifiers: marketInfo.specifiers,
            marketId: marketInfo.id
          },
          3
        )

        return !subscribedIgpomsTopicSet.value.has(topic)
      })
    })

    const getStickedHeaderHeight = () => {
      const pageHeaderHeight = document.querySelector('.m-detail-top-header')?.clientHeight ?? 0
      const teamsInfoHeight = document.querySelector('.m-live-summary .teams-info')?.clientHeight ?? 0
      const marketGroupNavHeight = document.querySelector('.m-detail-markets .m-sub-navs-wrapper')?.clientHeight ?? 0

      return pageHeaderHeight + teamsInfoHeight + marketGroupNavHeight
    }

    const isHeaderSticked = () => {
      const pageHeaderHeight = document.querySelector('.m-detail-top-header')?.clientHeight ?? 0
      const teamsInfoHeight = document.querySelector('.m-live-summary .teams-info')?.clientHeight ?? 0
      const marketGroupNavEl = document.querySelector('.m-detail-markets .m-sub-navs-wrapper')

      if (marketGroupNavEl == null) {
        return false
      }
      return marketGroupNavEl.getBoundingClientRect().top === pageHeaderHeight + teamsInfoHeight
    }

    const setFirstVisibleMarket = () => {
      if (marketItemRef.value == null || marketItemRef.value.length === 0) {
        firstVisibleMarketItemIndex.value = null
        return
      }

      const marketItemIndex = marketItemRef.value.findIndex((item) => {
        if (item == null || item.$el == null) {
          return false
        }

        if (item.$el.getBoundingClientRect == null) {
          return false
        }

        const marketItemHeaderHeight = item.$el.querySelector('.m-market-item--header')?.getBoundingClientRect()?.height ?? 0
        const visibleTopLimit = stickedHeaderHeight.value + marketItemHeaderHeight / 2
        return item.$el.getBoundingClientRect().top <= visibleTopLimit && item.$el.getBoundingClientRect().bottom > visibleTopLimit
      })

      if (marketItemIndex === -1) {
        firstVisibleMarketItemIndex.value = null
        return
      }

      firstVisibleMarketItemIndex.value = marketItemIndex
    }

    /**
     * Get the first visible market element. If the element is not found, get the last element
     * @returns {HTMLElement | null}
     */
    const getMarketItemScrollElement = () => {
      const el = marketItemRef.value[firstVisibleMarketItemIndex.value]?.$el
      if (el != null) {
        return el
      }

      return marketItemRef.value[marketItemRef.value.length - 1]?.$el
    }

    /**
     * @param {number | null} scrollYBeforeExpand
     */
    const scrollToFirstVisibleMarket = (scrollYBeforeExpand) => {
      if (marketItemRef.value == null || marketItemRef.value.length === 0 || firstVisibleMarketItemIndex.value == null) {
        // prevent chrome's scroll bar sticked at the bottom
        scrollYBeforeExpand != null && scrollTo(0, scrollYBeforeExpand)
        return
      }

      const el = getMarketItemScrollElement()
      if (!el) return
      const scrollY = el.getBoundingClientRect().top + window.scrollY - stickedHeaderHeight.value
      scrollTo(0, scrollY)
    }

    const getFirstMarketScrollY = () => {
      if (marketItemRef.value == null || marketItemRef.value.length === 0) {
        return 0
      }

      const el = marketItemRef.value[0]?.$el
      if (!el) return 0
      return el.getBoundingClientRect().top + window.scrollY - stickedHeaderHeight.value
    }

    const scrollToFirstMarket = () => {
      const firstMarketScrollY = getFirstMarketScrollY()
      scrollTo(0, firstMarketScrollY)
    }

    const handleCollapseTogglerClick = async (options) => {
      if (!isActivated.value) {
        return
      }

      const { shouldCollapse } = options

      setFirstVisibleMarket()

      if (shouldCollapse) {
        collapseAllMarkets({ marketGroup: props.name })
        nextTick(() => {
          scrollToFirstVisibleMarket()
        })
        return
      }

      marketListPrevHeight.value = marketListRef.value.getBoundingClientRect().height + 218
      const scrollYBeforeExpand = window.scrollY

      // All markets subscribed
      if (unSubscribedMarketIds.value.length === 0) {
        expandAllMarkets({ marketGroup: props.name })
        nextTick(() => {
          scrollToFirstVisibleMarket(scrollYBeforeExpand)
        })
        return
      }

      // Some markets not subscribed
      loading.value = true
      abortController = new AbortController()
      eventBus.$emit('update-mini-footer', false)
      try {
        await matchDetailStore.fetchMarketInfoBatch(fetchMarketInfoBatchBody.value, abortController.signal)
        expandAllMarkets({ marketGroup: props.name })
        nextTick(() => {
          scrollToFirstVisibleMarket(scrollYBeforeExpand)
        })
      } catch (error) {
        console.error(error)
      } finally {
        loading.value = false
      }
    }

    const updateScrollY = () => {
      if (!isActivated.value) {
        return
      }

      lastScrollY.value = window.scrollY
    }

    const restoreScrollY = () => {
      if (!isActivated.value) {
        return
      }

      if (isHeaderSticked()) {
        if (firstVisibleMarketItemIndex.value != null) {
          scrollToFirstVisibleMarket()
          return
        }

        if (lastScrollY.value == null || lastScrollY.value < getFirstMarketScrollY()) {
          scrollToFirstMarket()
          return
        }

        scrollTo(0, lastScrollY.value)
      }

      firstVisibleMarketItemIndex.value = null
      updateScrollY()
    }

    const favouriteImg = useThemeImage({
      light: {
        default: require('../images/default_favorite_markets_light.png')
      },
      dark: {
        default: require('../images/default_favorite_markets.png')
      }
    })

    onMounted(() => {
      eventBus.$on('collapse-toggler-click', handleCollapseTogglerClick)
      eventBus.$on('mini-footer-visible', restoreScrollY)
      stickedHeaderHeight.value = getStickedHeaderHeight()
      window.addEventListener('scroll', updateScrollY)
    })

    onBeforeUnmount(() => {
      eventBus.$off('collapse-toggler-click', handleCollapseTogglerClick)
      eventBus.$off('mini-footer-visible', restoreScrollY)
      abortController?.abort()
      window.removeEventListener('scroll', updateScrollY)
    })

    onActivated(() => {
      isActivated.value = true
      restoreScrollY()
    })

    onDeactivated(() => {
      isActivated.value = false
      abortController?.abort()
    })

    return {
      loading,
      event,
      favouriteImg,
      expandedMarketGroupMap,
      expandedMarketIdList,
      sortedMarketIds,
      firstVisibleMarketItemIndex,
      marketListRef,
      marketListPrevHeight,
      marketItemRef,
      handleToggleExpand,
      shouldMarketExpand,
      handleMarketItemActiveChange
    }
  },
  data() {
    return {
      show: true
    }
  },
  computed: {
    hasData() {
      const { marketIds } = this
      if (marketIds && marketIds.length) {
        const marketMap = this.event.markets.map
        // 有可投的 market
        return marketIds.some((id) => {
          const market = marketMap[id]
          if (Array.isArray(market)) {
            return market.some(isActiveMarket)
          } else {
            return isActiveMarket(market)
          }
        })
      }
      return false
    },
    showGuideImg() {
      return this.name === 'Favourites' && this.marketIds.length < 1
    }
  },
  watch: {
    hasData(val) {
      clearTimeout(this._timer)
      if (!val) {
        if (this.name !== 'Favourites') {
          this._timer = setTimeout(() => {
            this.show = val
          }, 5000)
        } else {
          this.show = false
        }
      } else {
        this.show = true
      }
    }
  },
  mounted() {
    if (!this.hasData) {
      this.show = false
    }
  },
  methods: {
    onFavourite(status) {
      this.$emit('favourite', status)
    }
    // checkIsActive (market) {
    //   if (Array.isArray(market)) {
    //     return market.some(x => +x.status === 0 || +x.status === 1)
    //   } else {
    //     return +market.status === 0 || +market.status === 1
    //   }
    // }
  }
}
</script>

<style lang="less">
@import url('~wap/common/style/variable.less');
@import url('../style/market.less');

.m-market-list {
  padding: 0 16px;

  &.favorite-guide {
    padding: 0 5 / @rem;
  }

  .m-market-item {
    &:last-child {
      border-bottom: 0;
    }
  }

  .img-container {
    margin-top: 25 / @rem;
    text-align: center;

    img {
      max-width: 100%;
      vertical-align: top;
    }
  }

  .market-list-loading-mask {
    width: 100%;
    background-color: #000;

    .m-block-loading {
      position: sticky;
      top: 182px;
    }
  }

  .m-market-range .market-range-bar {
    margin-left: 10px;
  }
}
</style>
<style lang="scss" scoped>
[data-mode='light'] .m-market-list {
  .market-list-loading-mask {
    background-color: #f8f8f8;
  }
}
</style>
